// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { InvoiceDetail } from '@/billing/api/types';
import { useRecordPayment } from '@/billing/hooks/use-record-payment';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDatePicker } from '@/components/form-date-picker';
import {
  SerializableCurrency,
  currencyInputSchema,
} from '@/currency/api/types';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { getCustomers } from '@/customer/api/get-customers';
import { Customer } from '@/customer/api/types';
import { CustomerAutocomplete } from '@/customer/components/customer-autocomplete';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

const recordPaymentFormSchema = z.object({
  amount: currencyInputSchema,
  paymentDate: dateSchema
    .nullable()
    .refine((value) => value !== null && value.isValid, {
      message: 'A payment date is required',
    }),
  customer: z.custom<Customer>().nullable(),
  reference: z.string(),
  memo: z.string(),
});

// Form state keeps the amount as the string/ISO pair FormCurrencyTextField
// binds to; the resolver output carries the parsed Decimal.
interface RecordPaymentFormData {
  amount: SerializableCurrency;
  paymentDate: DateTime | null;
  customer: Customer | null;
  reference: string;
  memo: string;
}

type RecordPaymentFormOutput = z.output<typeof recordPaymentFormSchema>;

// Pre-fills the amount input with the outstanding balance (the tax-inclusive
// number to collect), or an empty string when nothing is owed.
function defaultPaymentAmount(balance: Decimal): string {
  return balance.greaterThan(0) ? balance.toFixed(2) : '';
}

interface Props {
  isOpen: boolean;
  invoice: InvoiceDetail;
  onClose: () => void;
  onSuccess?: () => void;
  /**
   * Called for errors the dialog does not resolve locally (status-level
   * known codes and unknown/unexpected errors). The parent decides whether
   * to surface via the known-error snackbar or the support snackbar.
   */
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

export function RecordPaymentDialog(props: Props) {
  // The invoice's single currency drives both the locked ISO on the amount
  // input and the outstanding-balance display.
  const invoiceCurrency = props.invoice.currency;
  const balance = props.invoice.balance.amount;
  const balanceIsPositive = balance.greaterThan(0);
  // A string, not the Decimal: parseResponse mints a new Decimal on every
  // refetch, so an identity-keyed effect would reset the amount mid-typing.
  const defaultAmount = defaultPaymentAmount(balance);

  const [customerInitialized, setCustomerInitialized] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<RecordPaymentFormData, unknown, RecordPaymentFormOutput>({
    resolver: zodResolver(recordPaymentFormSchema),
    reValidateMode: 'onSubmit',
    defaultValues: {
      amount: { amount: defaultAmount, isoCode: invoiceCurrency },
      paymentDate: DateTime.now().startOf('day'),
      customer: null,
      reference: '',
      memo: '',
    },
  });

  const customersQuery = useQuery({
    queryKey: ['customer'],
    queryFn: () => getCustomers(),
    enabled: props.isOpen,
  });
  const customers = useMemo(
    () => customersQuery.data?.customers ?? [],
    [customersQuery.data]
  );

  // Initialize the customer selection from the invoice's customer once
  // the customer list loads
  useEffect(() => {
    if (
      !customerInitialized &&
      customers.length > 0 &&
      props.invoice.customerKey
    ) {
      const match = customers.find((c) => c.key === props.invoice.customerKey);
      if (match) {
        reset((current) => ({ ...current, customer: match }));
      }
      setCustomerInitialized(true);
    }
  }, [customers, props.invoice.customerKey, customerInitialized, reset]);

  // Reset the amount to the CURRENT outstanding balance every time the
  // dialog transitions closed → open. useForm captures `defaultValues`
  // only at first render, so without this a stale balance (e.g. from
  // before a line-item edit that added tax, or before another tab recorded
  // a partial payment) would remain in the input even after the invoice
  // prop refetched to a new value. balance already includes tax — the BE
  // stored `invoice.total` is SUM(line_total) where line_total is
  // total_price + tax_amount — so defaulting to balance is the right
  // tax-inclusive number to pre-fill.
  useEffect(() => {
    if (props.isOpen) {
      reset((current) => ({
        ...current,
        amount: { amount: defaultAmount, isoCode: invoiceCurrency },
      }));
    }
  }, [props.isOpen, defaultAmount, invoiceCurrency, reset]);

  const watchedAmount = watch('amount.amount');
  const parsedAmount = parseCurrencyAmount(watchedAmount) ?? new Decimal(0);
  const exceedsBalance = parsedAmount.greaterThan(balance) && balanceIsPositive;
  const exceedsBalanceMessage = `Exceeds balance by ${formatCurrencyValue({
    amount: parsedAmount.minus(balance),
    isoCode: invoiceCurrency,
  })}`;

  // Editing the amount clears whatever error the last submit left on it —
  // a resolver issue or a server rejection both describe the old value.
  // Declared before the exceeds-balance effect so that effect re-applies its
  // own error on the same render when the new value is still too large.
  useEffect(() => {
    clearErrors('amount.amount');
  }, [watchedAmount, clearErrors]);

  // Reactive exceeds-balance check: surface a field-level error on Amount as
  // the user types, not only on submit. Only manages the `client` error
  // type so Zod validation and backend errors keep their normal lifecycle.
  const amountErrorType = errors.amount?.amount?.type;
  useEffect(() => {
    if (exceedsBalance) {
      setError('amount.amount', {
        type: 'client',
        message: exceedsBalanceMessage,
      });
    } else if (amountErrorType === 'client') {
      clearErrors('amount.amount');
    }
  }, [
    exceedsBalance,
    exceedsBalanceMessage,
    amountErrorType,
    setError,
    clearErrors,
  ]);

  const recordMutation = useRecordPayment({
    invoiceKey: props.invoice.key,
    onSuccess: () => {
      reset({
        amount: { amount: '', isoCode: invoiceCurrency },
        paymentDate: DateTime.now().startOf('day'),
        customer: null,
        reference: '',
        memo: '',
      });
      setCustomerInitialized(false);
      props.onClose();
      props.onSuccess?.();
    },
    onError: (message, apiStatusCode, httpStatusCode) => {
      if (apiStatusCode === ERROR_CODE_MAPPING.plan_limit_exceeded) {
        return;
      }

      // Field-mappable known codes → helper text under the affected field.
      // Dialog stays open so the user can correct the input and retry.
      if (apiStatusCode === ERROR_CODE_MAPPING.payment_exceeds_balance) {
        setError('amount.amount', { type: 'server', message });
        return;
      }
      if (apiStatusCode === ERROR_CODE_MAPPING.invoice_customer_not_found) {
        setError('customer', { type: 'server', message });
        return;
      }
      // 404 (invoice gone) and known status conflicts (e.g. invoice_not_
      // payable — state transitioned out from under the user) close the
      // dialog. Retrying won't help; the parent navigates or surfaces a
      // known-error snackbar.
      const isKnownStatusConflict =
        apiStatusCode === ERROR_CODE_MAPPING.invoice_not_payable;
      if (httpStatusCode === 404 || isKnownStatusConflict) {
        props.onClose();
        props.onError?.(message, apiStatusCode, httpStatusCode);
        return;
      }
      // Unexpected errors (5xx / unknown) keep the dialog open — the user's
      // input is still valid and a retry is often the right move. Parent
      // shows the support snackbar above the dialog.
      props.onError?.(message, apiStatusCode, httpStatusCode);
    },
  });

  function handleClose() {
    if (recordMutation.isPending) {
      return;
    }
    reset({
      amount: { amount: defaultAmount, isoCode: invoiceCurrency },
      paymentDate: DateTime.now().startOf('day'),
      customer: null,
      reference: '',
      memo: '',
    });
    setCustomerInitialized(false);
    props.onClose();
  }

  function onSubmit(data: RecordPaymentFormOutput) {
    if (exceedsBalance) {
      // The reactive effect already set the field-level error.
      return;
    }
    if (
      data.paymentDate === null ||
      Number.isNaN(data.paymentDate.toMillis())
    ) {
      setError('paymentDate', {
        type: 'client',
        message: 'A payment date is required',
      });
      return;
    }
    recordMutation.mutate({
      invoiceKey: props.invoice.key,
      amount: data.amount,
      paymentDate: data.paymentDate,
      customerKey: data.customer?.key ?? null,
      reference: data.reference.trim() || null,
      memo: data.memo.trim() || null,
    });
  }

  return (
    <Dialog fullWidth maxWidth="xs" open={props.isOpen} onClose={handleClose}>
      <DialogTitle
        sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}
      >
        Record payment
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ py: 3 }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* Balance reference */}
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Outstanding balance
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                color={balanceIsPositive ? 'error.main' : 'success.main'}
              >
                {formatCurrencyValue({
                  amount: balance,
                  isoCode: invoiceCurrency,
                })}
              </Typography>
            </Stack>

            <FormCurrencyTextField
              name="amount"
              control={control}
              label="Amount"
              fullWidth
              lockTo={invoiceCurrency}
              allowZero={false}
              disabled={recordMutation.isPending}
            />

            <FormDatePicker
              name="paymentDate"
              control={control}
              label="Payment date"
              disableFuture
              isDisabled={recordMutation.isPending}
            />

            <Controller
              name="customer"
              control={control}
              render={({ field, fieldState }) => (
                <CustomerAutocomplete<Customer, false>
                  options={customers}
                  value={field.value}
                  onChange={(_event, newValue) => {
                    if (typeof newValue === 'string') {
                      return;
                    }
                    field.onChange(newValue);
                    if (fieldState.error) {
                      clearErrors('customer');
                    }
                  }}
                  label="Customer"
                  size="small"
                  disabled={recordMutation.isPending}
                  loading={customersQuery.isLoading}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <TextField
              label="Reference"
              size="small"
              fullWidth
              disabled={recordMutation.isPending}
              placeholder="e.g., CHK-1234"
              {...register('reference')}
            />

            <TextField
              label="Memo"
              size="small"
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              disabled={recordMutation.isPending}
              {...register('memo')}
            />
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Button onClick={handleClose} disabled={recordMutation.isPending}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={recordMutation.isPending}
          >
            Record payment
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
